// 🐻 "A mysterious bear chipped in" (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §3.6, Nina 09-29:
// "After recharge/fund add a 'mysterious bear' or whatever chipped in to keep
// '{SPACE NAME}' on! Notification at the top.").
//
// v1: Nina fires it by hand from the backend's admin op (handler.js `adminChipIn`)
// after a real payment; the room's sockets get { type: 'chip_in', id, animal } and
// the page shows ChipInToast for 8s. Page-only: residents never see money, and
// nothing here writes anything.
//
// ⚠️ PARITY: CHIP_IN_ANIMALS mirrors justsomeguy-backend/src/ChatGame/chipIn.js
// (the server picks the animal; this list only turns its key into words + an
// emoji). chipIn.test.js requires the backend module and fails on any drift.
// Always "a mysterious <animal>", lowercase, never joined into a visitor-style
// name ("MysteriousBear" is a real possible seat name), never an amount.

export const CHIP_IN_ANIMALS = Object.freeze([
  Object.freeze({ key: 'bear', emoji: '🐻' }),
  Object.freeze({ key: 'otter', emoji: '🦦' }),
  Object.freeze({ key: 'fox', emoji: '🦊' }),
  Object.freeze({ key: 'owl', emoji: '🦉' }),
  Object.freeze({ key: 'sloth', emoji: '🦥' }),
  Object.freeze({ key: 'frog', emoji: '🐸' }),
  Object.freeze({ key: 'hedgehog', emoji: '🦔' }),
  Object.freeze({ key: 'penguin', emoji: '🐧' }),
  Object.freeze({ key: 'panda', emoji: '🐼' }),
  Object.freeze({ key: 'bunny', emoji: '🐰' }),
  Object.freeze({ key: 'duckling', emoji: '🐤' }),
  Object.freeze({ key: 'turtle', emoji: '🐢' }),
]);

export const CHIP_IN_TOAST_MS = 8000;
const QUEUE_MAX = 3; // one toast at a time; a burst keeps at most three waiting
const BY_KEY = new Map(CHIP_IN_ANIMALS.map((a) => [a.key, a]));

/** The animal for a key; an unknown key (a newer backend) falls back to the bear. */
export function chipInAnimal(key) {
  return BY_KEY.get(String(key || '').toLowerCase()) || BY_KEY.get('bear');
}

const cleanRoom = (name) => String(name == null ? '' : name).replace(/\s+/g, ' ').trim().slice(0, 60);

/** "🐻 A mysterious bear chipped in. The AI Dorm stays on." */
export function chipInLine(animalKey, roomName) {
  const a = chipInAnimal(animalKey);
  const room = cleanRoom(roomName);
  return {
    emoji: a.emoji,
    text: `A mysterious ${a.key} chipped in. ${room || 'This room'} stays on.`,
  };
}

/**
 * A socket message → a toast item, or null. Only { type: 'chip_in' } with a
 * string id counts; nothing else in the payload is read (no name, no amount).
 */
export function chipInFromSocket(msg) {
  if (!msg || typeof msg !== 'object' || msg.type !== 'chip_in') return null;
  const id = typeof msg.id === 'string' && msg.id ? msg.id.slice(0, 80) : null;
  if (!id) return null;
  return { id, animal: chipInAnimal(msg.animal).key };
}

/**
 * The page's queue (pure): add an item unless it is already showing / waiting
 * (a socket can deliver twice), keep at most three. The head is the one on screen.
 * `seen` (optional, a Set the page keeps in a ref) also drops ids already shown
 * and dismissed; this adds the id to it.
 */
export function pushChipIn(queue, item, seen = null) {
  const q = Array.isArray(queue) ? queue : [];
  if (!item || !item.id || q.some((x) => x.id === item.id)) return q;
  if (seen && typeof seen.has === 'function' && seen.has(item.id)) return q;
  if (q.length >= QUEUE_MAX) return q;
  if (seen && typeof seen.add === 'function') seen.add(item.id);
  return [...q, item];
}

/** Drop the head (its 8s ran out, or ✕). */
export function shiftChipIn(queue) {
  return Array.isArray(queue) && queue.length ? queue.slice(1) : [];
}
